import type { AIProviderId } from '@creanizer/types'
import { getApiKeyProvider, type ApiKey, type BaseManager } from './types'

export class ApiKeysManager implements BaseManager {
  constructor(public db: IDBDatabase | null) {}

  private getDb(): IDBDatabase {
    if (!this.db) throw new Error('Database not initialized')
    return this.db
  }

  async addApiKey(
    apiKey: Omit<ApiKey, 'id' | 'createdAt' | 'usageCount'>
  ): Promise<ApiKey> {
    const newApiKey: ApiKey = {
      ...apiKey,
      id: crypto.randomUUID(),
      createdAt: new Date(),
      usageCount: 0,
    }
    return new Promise((resolve, reject) => {
      const transaction = this.getDb().transaction(['apiKeys'], 'readwrite')
      const request = transaction.objectStore('apiKeys').add(newApiKey)
      request.onsuccess = () => resolve(newApiKey)
      request.onerror = () => reject(new Error('Failed to add API key'))
    })
  }

  async getApiKeys(): Promise<ApiKey[]> {
    return new Promise((resolve, reject) => {
      const transaction = this.getDb().transaction(['apiKeys'], 'readonly')
      const index = transaction.objectStore('apiKeys').index('createdAt')
      const request = index.openCursor(null, 'prev')
      const apiKeys: ApiKey[] = []

      request.onsuccess = () => {
        const cursor = request.result
        if (!cursor) {
          resolve(apiKeys)
          return
        }
        apiKeys.push(cursor.value)
        cursor.continue()
      }
      request.onerror = () => reject(new Error('Failed to get API keys'))
    })
  }

  async getActiveApiKey(
    provider: AIProviderId = 'gemini'
  ): Promise<ApiKey | null> {
    return new Promise((resolve, reject) => {
      const transaction = this.getDb().transaction(['apiKeys'], 'readonly')
      const request = transaction.objectStore('apiKeys').getAll()
      request.onsuccess = () => {
        const apiKeys = request.result as ApiKey[]
        resolve(apiKeys.find(key =>
          key.isActive && getApiKeyProvider(key) === provider
        ) ?? null)
      }
      request.onerror = () => reject(new Error('Failed to get active API key'))
    })
  }

  async updateApiKey(id: string, updates: Partial<ApiKey>): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = this.getDb().transaction(['apiKeys'], 'readwrite')
      const store = transaction.objectStore('apiKeys')
      const request = store.get(id)
      request.onsuccess = () => {
        const apiKey = request.result as ApiKey | undefined
        if (!apiKey) {
          reject(new Error('API key not found'))
          return
        }
        const putRequest = store.put({ ...apiKey, ...updates })
        putRequest.onsuccess = () => resolve()
        putRequest.onerror = () => reject(new Error('Failed to update API key'))
      }
      request.onerror = () => reject(new Error('Failed to get API key'))
    })
  }

  async setActiveApiKey(id: string): Promise<void> {
    const apiKeys = await this.getApiKeys()
    const targetKey = apiKeys.find(apiKey => apiKey.id === id)
    if (!targetKey) throw new Error('API key not found')
    const targetProvider = getApiKeyProvider(targetKey)

    return new Promise((resolve, reject) => {
      const transaction = this.getDb().transaction(['apiKeys'], 'readwrite')
      const store = transaction.objectStore('apiKeys')
      for (const apiKey of apiKeys) {
        if (getApiKeyProvider(apiKey) === targetProvider) {
          store.put({ ...apiKey, isActive: apiKey.id === id })
        }
      }
      transaction.oncomplete = () => resolve()
      transaction.onerror = () =>
        reject(new Error('Failed to update API key status'))
      transaction.onabort = () =>
        reject(new Error('API key status update was aborted'))
    })
  }

  async deleteApiKey(id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = this.getDb().transaction(['apiKeys'], 'readwrite')
      const request = transaction.objectStore('apiKeys').delete(id)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(new Error('Failed to delete API key'))
    })
  }

  async incrementApiKeyUsage(id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = this.getDb().transaction(['apiKeys'], 'readwrite')
      const store = transaction.objectStore('apiKeys')
      const request = store.get(id)
      request.onsuccess = () => {
        const apiKey = request.result as ApiKey | undefined
        if (!apiKey) {
          resolve()
          return
        }
        const putRequest = store.put({
          ...apiKey,
          usageCount: apiKey.usageCount + 1,
          lastUsedAt: new Date(),
        })
        putRequest.onsuccess = () => resolve()
        putRequest.onerror = () => reject(new Error('Failed to update usage'))
      }
      request.onerror = () => reject(new Error('Failed to get API key'))
    })
  }
}
